/**
 * Commerce > Inventory. On-hand, committed, and available by item and
 * warehouse. For today, reads the Sage IM_ItemWarehouse mirror (Hammond's)
 * and the CounterPoint cp_inventory_mirror (Lolli & Pops); for a past date it
 * reads the daily inventory_snapshot table instead. Both via
 * GET /v1/commerce/catalog/inventory. Read-only.
 *
 * Columns: item (links to /commerce/products/:itemCode), description, brand,
 * warehouse, on-hand, committed, available.
 *
 * Description comes from each brand's item master and is LEFT JOINed by the API,
 * so it can be null; those rows render a muted "No description" rather than an
 * empty cell, which would read as a rendering bug. The item-code link resolves
 * for BOTH brands: the api detail handler falls back from the catalog cache to
 * the brand item masters and finally synthesizes a minimal record from warehouse
 * stock rows, so a code that exists only as residual stock lands on a real page
 * instead of a 404.
 * Filters: q (matches item code or description, mirroring Commerce > Products),
 * brand, warehouse, low_stock, in_stock (in_stock defaults ON), date (defaults
 * to today; a past day reads the inventory_snapshot table server-side instead
 * of the live mirrors, bounded to the earliest date
 * useCatalogInventorySnapshotDates reports).
 *
 * The subscribe control captures whatever filters are applied right now and
 * saves them with the subscription, so the emailed sheet contains only the
 * matching rows. date is deliberately excluded from that capture: the daily
 * email always describes the current day, never a historical one.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Loader2, Mail } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useSession } from '@/lib/useSession';
import {
  INVENTORY_SNAPSHOT_REPORT_TYPE,
  useCreateReportSubscription,
  useReportSubscriptions,
} from '@/lib/useReportSubscriptions';
import {
  catalogBrandTone,
  formatCatalogQty,
  formatWarehouse,
  hasCommerceInventory,
  labelCatalogBrand,
  useCatalogInventory,
  useCatalogInventorySnapshotDates,
  useCatalogLocations,
  formatLocationLabel,
  CATALOG_BRAND_OPTIONS,
  type CatalogBrand,
  type InventoryFilters,
  type InventoryRow,
} from '@/lib/useCatalog';

/** Today, as YYYY-MM-DD in the browser's local time. */
function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type InvSortColumn = 'item_code' | 'description' | 'brand' | 'warehouse' | 'on_hand' | 'committed' | 'available';

const SORTABLE: ReadonlySet<InvSortColumn> = new Set<InvSortColumn>([
  'item_code', 'description', 'brand', 'warehouse', 'on_hand', 'committed', 'available',
]);

function parseSort(raw: string | null): { column: InvSortColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (!SORTABLE.has(col as InvSortColumn)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as InvSortColumn, dir };
}

function sortValue(r: InventoryRow, col: InvSortColumn): string | number {
  switch (col) {
    case 'item_code': return r.item_code.toLowerCase();
    // Missing descriptions sort last in ascending order rather than first,
    // so the rows worth reading are not buried under the blanks.
    case 'description': return (r.description ?? '￿').toLowerCase();
    case 'brand': return r.brand.toLowerCase();
    case 'warehouse': return r.warehouse.toLowerCase();
    case 'on_hand': return Number(r.on_hand ?? 0);
    case 'committed': return Number(r.committed ?? 0);
    case 'available': return Number(r.available ?? 0);
  }
}

/**
 * Daily-email subscription control for this grid. Unlike the OMNI control on
 * Retail > Performance, this one is CREATE-ONLY: many subscriptions per person
 * per report are allowed now, each carrying its own saved filters, so there is
 * no on/off toggle to flip here. Removing one happens on Settings, which is
 * where the caller can see all of them together.
 *
 * Hides itself entirely when the subscriptions endpoint is unavailable
 * (404 / 503 / shape mismatch), matching the page's degrade-gracefully posture
 * while the api side ships in parallel.
 */
function InventorySubscribeControl({ filters }: { filters: InventoryFilters }) {
  const { available } = useReportSubscriptions();
  const createMut = useCreateReportSubscription();
  const [feedback, setFeedback] = useState<{ kind: 'saved' | 'error'; msg: string } | null>(null);

  // Clear the transient saved/error note after a few seconds.
  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 4000);
    return () => window.clearTimeout(t);
  }, [feedback]);

  // Guard after the hooks so hook order stays stable across renders.
  //
  // NEVER return null here. This control used to hide itself whenever
  // GET /v1/reports/subscriptions failed, which on dev 2026-07-28 meant the
  // Subscribe strip was simply absent from the page: the /v1/reports/* prefix was
  // not routed at the edge, so the endpoint 404'd and the shipped feature looked
  // like it had never been built. A feature that disappears on error is
  // indistinguishable from one that does not exist, and it costs someone a
  // screenshot and a round trip to find out which. Show a disabled control and
  // name the problem instead.
  if (!available) {
    return (
      <div className="shrink-0 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amp-line bg-amp-white px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <span className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Daily email for this view is unavailable right now, so you cannot subscribe. Nothing is
          wrong with the inventory figures above.
        </span>
        <div className="ml-auto">
          <button
            type="button"
            disabled
            title="The subscriptions service did not respond. Try again shortly; if it persists, tell IT."
            className="inline-flex cursor-not-allowed items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <Mail className="h-3 w-3" aria-hidden />
            Subscribe
          </button>
        </div>
      </div>
    );
  }

  const isSaving = createMut.isPending;
  const hasFilters = Boolean(filters.q || filters.brand || filters.warehouse || filters.low_stock) || filters.in_stock === false;

  const handleSubscribe = () => {
    if (isSaving) return;
    createMut.mutate(
      { reportType: INVENTORY_SNAPSHOT_REPORT_TYPE, filters: { ...filters } },
      {
        onSuccess: () =>
          setFeedback({ kind: 'saved', msg: 'Subscribed. Manage it in Settings.' }),
        onError: (e) =>
          setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not subscribe' }),
      },
    );
  };

  const Icon = isSaving ? Loader2 : Mail;

  return (
    <div className="shrink-0 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amp-line bg-amp-white px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <span className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Get this view by email every morning. The attached sheet holds only the rows matching{' '}
        {hasFilters ? 'the filters you have applied' : 'this view'}.
      </span>
      <div className="ml-auto flex items-center gap-2">
        {feedback && (
          <span
            role="status"
            className={
              feedback.kind === 'error'
                ? 'text-[12px] font-semibold text-red-700 dark:text-red-300'
                : 'inline-flex items-center gap-1 text-[12px] font-semibold text-amp-ink dark:text-amp-dark-ink'
            }
          >
            {feedback.kind === 'saved' && <Check className="h-3 w-3" aria-hidden />}
            {feedback.msg}
          </span>
        )}
        <Link
          to="/settings"
          className="text-[12px] text-amp-ink-muted underline hover:text-amp-ink hover:no-underline dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          Manage
        </Link>
        <button
          type="button"
          onClick={handleSubscribe}
          disabled={isSaving}
          title="Saves the filters currently applied. The daily email contains only the rows matching them."
          className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          <Icon className={`h-3 w-3 ${isSaving ? 'animate-spin' : ''}`} aria-hidden />
          {isSaving ? 'Subscribing...' : 'Subscribe'}
        </button>
      </div>
    </div>
  );
}

export function CommerceCatalogInventory() {
  const { user } = useSession();
  const canRead = hasCommerceInventory(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const brand = searchParams.get('brand') ?? '';
  const warehouse = searchParams.get('warehouse') ?? '';
  const lowStock = searchParams.get('low_stock') === 'true';
  // In-stock-only is ON unless the URL explicitly turns it off.
  const inStock = searchParams.get('in_stock') !== 'false';
  const today = todayIsoDate();
  const date = searchParams.get('date') || today;
  const isHistorical = date !== today;

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const setStr = (k: string, v: string) => update((sp) => (v ? sp.set(k, v) : sp.delete(k)));
  const setBool = (k: string, v: boolean) => update((sp) => (v ? sp.set(k, 'true') : sp.delete(k)));
  // in_stock is on by default, so the URL carries the OFF state.
  const setInStock = (v: boolean) => update((sp) => (v ? sp.delete('in_stock') : sp.set('in_stock', 'false')));
  // today is on by default, so the URL only carries a date when it differs.
  const setDate = (v: string) => update((sp) => (v && v !== today ? sp.set('date', v) : sp.delete('date')));
  const resetFilters = () =>
    update((sp) => ['q', 'brand', 'warehouse', 'low_stock', 'in_stock', 'date', 'sort'].forEach((k) => sp.delete(k)));

  const filters: InventoryFilters = useMemo(
    () => ({
      q: q || undefined,
      brand: (brand || undefined) as CatalogBrand | undefined,
      warehouse: warehouse || undefined,
      low_stock: lowStock || undefined,
      in_stock: inStock,
    }),
    [q, brand, warehouse, lowStock, inStock],
  );

  // Warehouse picker options, sourced from the api pickers endpoint rather than a
  // hard-coded list (standing rule: picker dropdowns over free-text foreign-key
  // codes). Brand-aware: when the Brand filter is set the list narrows to that
  // brand, because Hammond's 3-digit warehouse codes and Lolli's 4-digit store
  // numbers are separate namespaces and mixing them offers meaningless choices.
  const locationsQ = useCatalogLocations((brand || undefined) as CatalogBrand | undefined);
  const warehouseOptions = useMemo(
    () =>
      locationsQ.locations.map((l) => ({
        value: l.code,
        label: formatLocationLabel(l),
        // Brand only matters on the unfiltered list, where both namespaces show.
        ...(brand ? {} : { sublabel: labelCatalogBrand(l.brand) }),
      })),
    [locationsQ.locations, brand],
  );

  // A warehouse chosen under one brand may not exist under another, so changing
  // Brand clears a now-impossible warehouse rather than silently returning zero
  // rows with a filter the picker cannot even display.
  const changeBrand = (next: string) => {
    update((sp) => {
      if (next) sp.set('brand', next);
      else sp.delete('brand');
      sp.delete('warehouse');
    });
  };

  // Bounds the date picker to days that actually have a snapshot. Until the api
  // ships this endpoint, `available` is false and minDate stays pinned to
  // today: sending a past `date` to an api that does not yet honor it would
  // silently re-show today's live data mislabeled as a historical snapshot,
  // which is worse than the picker simply refusing to offer past days yet.
  const snapshotDatesQ = useCatalogInventorySnapshotDates();
  const minDate = snapshotDatesQ.available && snapshotDatesQ.earliestDate ? snapshotDatesQ.earliestDate : today;
  // A hand-edited or bookmarked URL can still carry a past `date` even while the
  // picker itself is pinned to today (see minDate above). Guard here too, so
  // that link never fetches with a `date` the api may not honor yet.
  const historicalUnavailable = isHistorical && !snapshotDatesQ.available;

  const inventoryQ = useCatalogInventory(filters, historicalUnavailable ? undefined : date);

  const sort = parseSort(searchParams.get('sort'));
  const toggleSort = (col: InvSortColumn) => {
    if (!sort || sort.column !== col) update((sp) => sp.set('sort', `${col}:asc`));
    else if (sort.dir === 'asc') update((sp) => sp.set('sort', `${col}:desc`));
    else update((sp) => sp.delete('sort'));
  };
  const sortedFor = (col: InvSortColumn): SortDir | null => (sort && sort.column === col ? sort.dir : null);

  if (user && !canRead) return <AccessDeniedCard />;

  const rows = sort
    ? [...inventoryQ.rows].sort((a, b) => {
        const av = sortValue(a, sort.column);
        const bv = sortValue(b, sort.column);
        const f = sort.dir === 'asc' ? 1 : -1;
        if (av < bv) return -1 * f;
        if (av > bv) return 1 * f;
        return 0;
      })
    : inventoryQ.rows;

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div>
        <label htmlFor="inv-date" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Date</label>
        <input
          id="inv-date"
          type="date"
          value={date}
          max={today}
          min={minDate}
          title={
            minDate === today
              ? 'Historical dates open up once the snapshot history is available.'
              : `Snapshots go back to ${minDate}.`
          }
          onChange={(e) => setDate(e.target.value)}
          className="border border-amp-line rounded px-2 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </div>
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="q" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="q"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="Item code or description..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <label htmlFor="brand" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Brand</label>
        <select
          id="brand"
          value={brand}
          onChange={(e) => changeBrand(e.target.value)}
          className="min-w-[150px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {CATALOG_BRAND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Warehouse</label>
        <SearchableSelect
          options={warehouseOptions}
          selected={warehouse ? [warehouse] : []}
          onChange={(next) => setStr('warehouse', next[0] ?? '')}
          multiSelect={false}
          placeholder="Any"
          ariaLabel="Filter by warehouse or store"
        />
      </div>
      <button
        type="button"
        onClick={() => setInStock(!inStock)}
        aria-pressed={inStock}
        title="On by default. Turn off to include every item and location the mirrors carry, most of which hold no stock."
        className={`text-xs rounded px-3 py-1.5 border ${
          inStock
            ? 'border-amp-ink bg-amp-ink text-amp-white dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface'
            : 'border-amp-line text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm'
        }`}
      >
        In stock only
      </button>
      <button
        type="button"
        onClick={() => setBool('low_stock', !lowStock)}
        aria-pressed={lowStock}
        className={`text-xs rounded px-3 py-1.5 border ${
          lowStock
            ? 'border-amp-ink bg-amp-ink text-amp-white dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface'
            : 'border-amp-line text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm'
        }`}
      >
        Low stock only
      </button>
      {(q || brand || warehouse || lowStock || !inStock || isHistorical) && (
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Commerce</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Inventory</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {historicalUnavailable
              ? 'On-hand, committed, and available by item and warehouse. Historical dates are not available yet.'
              : isHistorical
                ? `On-hand, committed, and available by item and warehouse, as captured on ${date}.`
                : 'On-hand, committed, and available by item and warehouse.'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="inventory" toolName="Inventory" />
        </div>
      </div>

      <InventorySubscribeControl filters={filters} />

      <DataGridShell
        total={inventoryQ.totalCount}
        loaded={rows.length}
        countNoun="inventory rows"
        toolbar={filterBar}
        onReachEnd={inventoryQ.fetchNextPage}
        hasMore={inventoryQ.hasNextPage}
        isLoadingMore={inventoryQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader label="Item Code" sorted={sortedFor('item_code')} onToggle={() => toggleSort('item_code')} thClassName="px-3 py-2" />
              <SortableHeader label="Description" sorted={sortedFor('description')} onToggle={() => toggleSort('description')} thClassName="px-3 py-2 min-w-[220px]" />
              <SortableHeader label="Brand" sorted={sortedFor('brand')} onToggle={() => toggleSort('brand')} thClassName="px-3 py-2 w-[130px]" />
              <SortableHeader label="Warehouse" sorted={sortedFor('warehouse')} onToggle={() => toggleSort('warehouse')} thClassName="px-3 py-2 w-[130px]" />
              <SortableHeader label="On Hand" sorted={sortedFor('on_hand')} onToggle={() => toggleSort('on_hand')} align="right" thClassName="px-3 py-2 w-[110px]" />
              <SortableHeader label="Committed" sorted={sortedFor('committed')} onToggle={() => toggleSort('committed')} align="right" thClassName="px-3 py-2 w-[110px]" />
              <SortableHeader label="Available" sorted={sortedFor('available')} onToggle={() => toggleSort('available')} align="right" thClassName="px-3 py-2 w-[110px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {historicalUnavailable ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Historical inventory by date is not available yet. Pick today to see live figures.
                </td>
              </tr>
            ) : inventoryQ.isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading inventory...</td></tr>
            ) : inventoryQ.error ? (
              <tr><td colSpan={7} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {inventoryQ.error.message}</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {isHistorical && !(q || brand || warehouse || lowStock || !inStock)
                    ? 'No snapshot captured for this date. The nightly job may have missed it.'
                    : 'No inventory matches this filter.'}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={`${row.item_code}:${row.warehouse}:${i}`} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                  <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
                    <Link to={`/commerce/products/${encodeURIComponent(row.item_code.trim())}`} className="hover:underline">
                      {row.item_code}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                    {row.description?.trim() ? (
                      row.description
                    ) : (
                      <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted" title="This item has no description in its brand item master.">
                        No description
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2"><Pill tone={catalogBrandTone(row.brand)}>{labelCatalogBrand(row.brand)}</Pill></td>
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{formatWarehouse(row.warehouse, row.warehouse_name)}</td>
                  <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(row.on_hand)}</td>
                  <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(row.committed)}</td>
                  <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(row.available)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Inventory is gated by the commerce:read capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
